import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { Search, DollarSign, User, Calendar, CheckCircle, AlertTriangle, Package, Clock, FileText, Zap } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import InstallationInvoiceView from '../components/InstallationInvoiceView';

const QuickCollection = () => {
  const [searchParams] = useSearchParams();
  const preselectedCustomer = searchParams.get('customer');

  const [searchTerm, setSearchTerm] = useState(preselectedCustomer || '');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [amount, setAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [transactionId, setTransactionId] = useState('');
  const [proofImage, setProofImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [installInvoice, setInstallInvoice] = useState(null);
  const [showInvoice, setShowInvoice] = useState(false);

  // Split payment state
  const [splitAvailable, setSplitAvailable] = useState(false);
  const [splitGroupId, setSplitGroupId] = useState('');
  const [splitExistingCustomer, setSplitExistingCustomer] = useState('');

  // Advance utilization state
  const [advanceLoading, setAdvanceLoading] = useState(false);

  // Auto-load if preselected from dashboard link
  useEffect(() => {
    const loadPreselectedCustomer = async () => {
      if (!preselectedCustomer) return;
      try {
        const res = await api.get(`/customers/${preselectedCustomer}/`);
        selectCustomer(res.data);
      } catch (err) {
        handleSearch(preselectedCustomer);
      }
    };

    loadPreselectedCustomer();
  }, [preselectedCustomer]);

  const searchTimeout = useRef(null);
  const abortControllerRef = useRef(null);

  const handleSearch = (query) => {
    setSearchTerm(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    // Debounce: wait 300ms after last keystroke
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(async () => {
      // Cancel previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      
      try {
        const res = await api.get(`/customers/?search=${query}`, {
          signal: abortControllerRef.current.signal
        });
        setSearchResults(res.data.results || []);
      } catch (err) {
        // Don't show error if request was cancelled
        if (err.code !== 'ERR_CANCELED') {
          console.error("Search Error:", err);
          setErrorMsg("Failed to search customers. Check connection.");
        }
      }
    }, 300);
  };

  // Cleanup: abort requests on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const fetchInstallInvoice = async (customerId) => {
    try {
      const res = await api.get(`/installation-invoices/by-customer/${customerId}/`);
      setInstallInvoice(res.data);
    } catch {
      setInstallInvoice(null);
    }
  };

  const selectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setSearchResults([]);
    setSearchTerm(customer.username);

    // Auto-fill: total due (installation + monthly combined)
    const due = parseFloat(customer.total_due) || 0;
    const price = parseFloat(customer.monthly_price) || 0;
    setAmount(due > 0 ? due : price);

    setSuccessMsg('');
    setErrorMsg('');
    setTransactionId('');
    setProofImage(null);
    setPreviewUrl('');
    setShowInvoice(false);
    setSplitAvailable(false);
    setSplitGroupId('');
    setSplitExistingCustomer('');

    // Fetch installation invoice if customer is pending
    if (customer.status === 'pending') {
      fetchInstallInvoice(customer.id);
    } else {
      setInstallInvoice(null);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProofImage(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  // DRF wraps ValidationError dict values in arrays — normalise to scalar
  const unwrap = (v) => (Array.isArray(v) ? v[0] : v);

  const parseApiError = (err) => {
    const data = err.response?.data;
    if (!data) return 'Something went wrong. Please try again.';

    // Split payment offer — not a real error, handled separately
    if (unwrap(data.proof_image) === 'SPLIT_AVAILABLE') return null;

    if (data.proof_image) return unwrap(data.proof_image);
    if (data.detail) return data.detail;
    const msgs = Object.entries(data)
      .map(([f, errs]) => `${f}: ${Array.isArray(errs) ? errs[0] : errs}`)
      .join(' · ');
    return msgs || JSON.stringify(data);
  };

  const submitCollection = async (isSplit = false) => {
    if (!selectedCustomer || !amount || selectedCustomer.paid_this_month) return;

    if (paymentMethod !== 'cash' && !proofImage) {
      setErrorMsg('A payment screenshot is required for JazzCash, EasyPaisa, and Bank Transfer.');
      return;
    }

    setLoading(true);
    setErrorMsg('');
    setSplitAvailable(false);

    const formData = new FormData();
    formData.append('customer', selectedCustomer.id);
    formData.append('amount_collected', amount);
    formData.append('payment_method', paymentMethod);
    formData.append('notes', 'Collected via Officer Dashboard');

    if (transactionId) formData.append('transaction_id', transactionId);
    if (proofImage) formData.append('proof_image', proofImage);
    if (isSplit) {
      formData.append('is_split_payment', 'true');
      if (splitGroupId) formData.append('split_group_id', splitGroupId);
    }

    try {
      const res = await api.post('/collections/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const proofStatus = res.data?.payment_proof_status;
      const splitNote = isSplit ? ' · Split Payment' : '';
      const verifiedNote = proofStatus === 'verified' ? ' · Screenshot Verified ✓' : '';
      setSuccessMsg(`Successfully collected PKR ${amount}${verifiedNote}${splitNote}`);

      if (selectedCustomer.status === 'pending') {
        await fetchInstallInvoice(selectedCustomer.id);
      }

      setSelectedCustomer(null);
      setSearchTerm('');
      setAmount('');
      setTransactionId('');
      setProofImage(null);
      setPreviewUrl('');
      setSplitAvailable(false);
      setSplitGroupId('');
      setSplitExistingCustomer('');
      setTimeout(() => setSuccessMsg(''), 10000);
    } catch (err) {
      console.error(err);
      const data = err.response?.data;
      if (unwrap(data?.proof_image) === 'SPLIT_AVAILABLE') {
        setSplitAvailable(true);
        setSplitGroupId(unwrap(data.split_group_id) || '');
        setSplitExistingCustomer(unwrap(data.existing_customer) || 'another customer');
      } else {
        setErrorMsg(parseApiError(err));
      }
    } finally {
      setLoading(false);
    }
  };

  const applyAdvance = async () => {
    if (!selectedCustomer) return;
    setAdvanceLoading(true);
    setErrorMsg('');
    try {
      const res = await api.post('/collections/apply-advance/', { customer_id: selectedCustomer.id });
      setSuccessMsg(
        `Advance applied — PKR ${res.data.amount_applied?.toLocaleString()} for ${res.data.invoice_month}. ` +
        `Credit remaining: PKR ${res.data.credit_remaining?.toLocaleString()}`
      );
      setSelectedCustomer(null);
      setSearchTerm('');
      setAmount('');
      setTimeout(() => setSuccessMsg(''), 12000);
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || 'Failed to apply advance.');
    } finally {
      setAdvanceLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    submitCollection(false);
  };

  const instDue = parseFloat(selectedCustomer?.installation_due) || 0;
  const monDue = parseFloat(selectedCustomer?.monthly_due) || 0;
  const creditBal = parseFloat(selectedCustomer?.credit_balance) || 0;
  const hasInstallDue = instDue > 0;
  const isPaidThisMonth = Boolean(selectedCustomer?.paid_this_month);
  const canApplyAdvance = creditBal > 0 && creditBal >= monDue && monDue > 0 && !isPaidThisMonth;
  const paidReasonText = selectedCustomer?.paid_this_month_reason === 'invoice'
    ? 'Current-month invoice is already paid.'
    : selectedCustomer?.paid_this_month_reason === 'collection'
      ? 'A collection is already recorded for this month.'
      : selectedCustomer?.paid_this_month_reason === 'collection_and_invoice'
        ? 'A collection and paid invoice already exist for this month.'
        : 'This customer is already paid for the current month.';

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6">
      <h1 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
        <DollarSign className="text-green-600" /> Quick Collection Entry
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Left Column: Search & Customer Details */}
        <div className="lg:col-span-2 space-y-6">

          {/* Search Box */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 relative">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Search Customer</label>
            <div className="relative">
              <Search className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Type username (e.g., 8city143) or name..."
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
              />
            </div>

            {/* Search Results Dropdown */}
            {searchResults.length > 0 && (
              <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-64 overflow-y-auto">
                {searchResults.map(cust => (
                  <div
                    key={cust.id}
                    onClick={() => selectCustomer(cust)}
                    className="p-3 hover:bg-blue-50 cursor-pointer border-b border-gray-50 last:border-0 flex justify-between items-center group"
                  >
                    <div>
                      <p className="font-bold text-gray-800 group-hover:text-blue-700">{cust.username}</p>
                      <p className="text-xs text-gray-500">{cust.name}</p>
                    </div>
                    <div className="text-right space-y-1">
                      {cust.paid_this_month && (
                        <div>
                          <span className="text-xs px-2 py-1 rounded-full font-bold bg-green-100 text-green-700">PAID</span>
                        </div>
                      )}
                      <span className={`text-xs px-2 py-1 rounded-full font-bold ${
                        cust.status === 'active' ? 'bg-green-100 text-green-700' :
                        cust.status === 'pending' ? 'bg-blue-100 text-blue-700' :
                        cust.status === 'expired' ? 'bg-yellow-100 text-yellow-700' :
                        cust.status === 'suspended' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300' :
                        cust.status === 'churned' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {cust.status.toUpperCase()}
                      </span>
                      <p className="text-xs text-gray-400 mt-1">{cust.area_name}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Customer Billing Details Card */}
          {selectedCustomer && (
            <div className="bg-white p-6 rounded-xl shadow-sm border border-blue-100 animate-fade-in">
              <div className="flex justify-between items-start mb-6 border-b border-gray-100 pb-4">
                <div>
                  <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <User size={18} className="text-blue-600" />
                    {selectedCustomer.name}
                  </h3>
                  <p className="text-sm text-gray-500 ml-7">{selectedCustomer.username}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">Total Due</p>
                  <p className="text-2xl font-bold text-red-600">PKR {selectedCustomer.total_due}</p>
                </div>
              </div>

              {isPaidThisMonth && (
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2 text-green-700 font-bold">
                    <CheckCircle size={18} /> PAID
                  </div>
                  <p className="text-sm text-green-700 mt-1">{paidReasonText}</p>
                </div>
              )}

              {/* Advance credit banner */}
              {creditBal > 0 && !isPaidThisMonth && (
                <div className={`mb-4 p-4 rounded-lg border ${canApplyAdvance ? 'bg-blue-50 border-blue-200' : 'bg-amber-50 border-amber-200'}`}>
                  <div className={`flex items-center justify-between gap-2 ${canApplyAdvance ? 'text-blue-700' : 'text-amber-700'}`}>
                    <div>
                      <p className="font-bold text-sm flex items-center gap-1">
                        <Zap size={15} /> Advance Credit Available
                      </p>
                      <p className="text-sm mt-0.5">PKR {creditBal.toLocaleString()} on account</p>
                      {!canApplyAdvance && monDue > 0 && (
                        <p className="text-xs mt-1 text-amber-600">
                          Credit (PKR {creditBal.toLocaleString()}) &lt; Due (PKR {monDue.toLocaleString()}) — collect the shortfall in cash.
                        </p>
                      )}
                    </div>
                    {canApplyAdvance && (
                      <button
                        type="button"
                        onClick={applyAdvance}
                        disabled={advanceLoading}
                        className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white text-sm font-bold rounded-lg hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap"
                      >
                        {advanceLoading
                          ? <><div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent" /> Applying…</>
                          : <><Zap size={14} /> Apply Advance</>
                        }
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Due Breakdown — shows separate amounts for pending customers */}
              {hasInstallDue && (
                <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <h4 className="text-xs font-bold text-amber-700 uppercase mb-2">Payment Breakdown</h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Installation Charges:</span>
                      <span className="font-semibold text-amber-800">PKR {instDue.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Monthly Package:</span>
                      <span className="font-semibold text-amber-800">PKR {monDue.toLocaleString()}</span>
                    </div>
                    <div className="border-t border-amber-300 pt-1 flex justify-between">
                      <span className="font-bold text-gray-800">Total Due:</span>
                      <span className="font-bold text-red-600">PKR {(instDue + monDue).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                  <div className="flex items-center gap-2 text-gray-500 mb-1">
                    <Package size={14} /> <span className="text-xs font-bold uppercase">Package</span>
                  </div>
                  <p className="font-semibold text-gray-800 text-sm truncate">{selectedCustomer.package_name}</p>
                </div>

                <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                  <div className="flex items-center gap-2 text-gray-500 mb-1">
                    <DollarSign size={14} /> <span className="text-xs font-bold uppercase">Price</span>
                  </div>
                  <p className="font-semibold text-gray-800 text-sm">PKR {selectedCustomer.monthly_price}</p>
                </div>

                <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                  <div className="flex items-center gap-2 text-gray-500 mb-1">
                    <Clock size={14} /> <span className="text-xs font-bold uppercase">Expiry</span>
                  </div>
                  <p className={`font-semibold text-sm ${new Date(selectedCustomer.expiry_date) < new Date() ? 'text-red-600' : 'text-green-600'}`}>
                    {selectedCustomer.expiry_date}
                  </p>
                </div>

                <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                  <div className="flex items-center gap-2 text-gray-500 mb-1">
                    <Calendar size={14} /> <span className="text-xs font-bold uppercase">Area</span>
                  </div>
                  <p className="font-semibold text-gray-800 text-sm truncate">{selectedCustomer.area_name}</p>
                </div>
              </div>

              {/* View Installation Invoice button */}
              {installInvoice && (
                <div className="mt-4 pt-3 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => setShowInvoice(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700"
                  >
                    <FileText size={14} />
                    View Installation Invoice
                    <span className={`ml-1 text-[10px] px-1.5 py-0.5 rounded-full ${
                      installInvoice.status === 'paid' ? 'bg-green-200 text-green-800' :
                      installInvoice.status === 'partial' ? 'bg-yellow-200 text-yellow-800' :
                      'bg-red-200 text-red-800'
                    }`}>
                      {installInvoice.status.toUpperCase()}
                    </span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Column: Payment Form */}
        <div className="lg:col-span-1">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 sticky top-24">
            <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
              <CheckCircle size={18} className="text-green-600" /> Record Payment
            </h3>

            {successMsg && (
              <div className="mb-4 p-3 bg-green-50 text-green-700 text-sm rounded-lg border border-green-100 flex items-start gap-2">
                <CheckCircle size={16} className="mt-0.5 flex-shrink-0" />
                <span>{successMsg}</span>
              </div>
            )}

            {errorMsg && (
              <div className="mb-4 p-3 bg-red-50 text-red-700 text-sm rounded-lg border border-red-100 flex items-start gap-2">
                <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
                <span>{errorMsg}</span>
              </div>
            )}

            {splitAvailable && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-300 rounded-lg text-sm">
                <div className="flex items-start gap-2 text-amber-800 font-semibold mb-2">
                  <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
                  <span>
                    This screenshot was already used for customer <strong>{splitExistingCustomer}</strong>.
                    Is this a split payment covering multiple customers?
                  </span>
                </div>
                <div className="flex gap-2 mt-2">
                  <button
                    type="button"
                    onClick={() => submitCollection(true)}
                    className="px-3 py-1.5 bg-amber-600 text-white text-xs font-bold rounded-lg hover:bg-amber-700"
                  >
                    Yes, add as split payment
                  </button>
                  <button
                    type="button"
                    onClick={() => { setSplitAvailable(false); setSplitGroupId(''); setSplitExistingCustomer(''); }}
                    className="px-3 py-1.5 bg-gray-200 text-gray-700 text-xs font-bold rounded-lg hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase">Amount (PKR)</label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-gray-400 font-bold">Rs</span>
                  <input
                    type="number"
                    required
                    step="0.01"
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none font-bold text-gray-800"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    disabled={!selectedCustomer || isPaidThisMonth}
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase">Payment Method</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none bg-white"
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  disabled={!selectedCustomer || isPaidThisMonth}
                >
                  <option value="cash">Cash</option>
                  <option value="jazzcash">JazzCash</option>
                  <option value="easypaisa">EasyPaisa</option>
                  <option value="bank">Bank Transfer</option>
                </select>
              </div>

              {(paymentMethod === 'jazzcash' || paymentMethod === 'easypaisa' || paymentMethod === 'bank') && (
                <div className="space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase">
                      Transaction ID / Last 4 Digits
                      <span className="ml-1 text-gray-400 font-normal normal-case">(optional)</span>
                    </label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                      value={transactionId}
                      onChange={(e) => setTransactionId(e.target.value)}
                      disabled={!selectedCustomer || isPaidThisMonth}
                      placeholder="e.g., 8X9Z or 1234 — leave blank if not on screenshot"
                    />
                    <p className="text-[11px] text-gray-400 mt-1">
                      Not visible? Share the "View Receipt" screenshot for a transaction ID.
                    </p>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase">Upload Screenshot</label>
                    <input
                      type="file"
                      accept="image/*"
                      className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      onChange={handleImageChange}
                      disabled={!selectedCustomer || isPaidThisMonth}
                    />
                    {previewUrl && (
                      <img src={previewUrl} alt="Proof" className="mt-2 h-20 w-auto rounded border border-gray-200" />
                    )}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase">Notes (Optional)</label>
                <textarea
                  rows="2"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none text-sm"
                  placeholder="Any specific remarks..."
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={!selectedCustomer || loading || isPaidThisMonth}
                className="w-full bg-green-600 text-white font-bold py-3 rounded-lg hover:bg-green-700 transition-all shadow-md shadow-green-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none flex items-center justify-center gap-2 mt-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    {paymentMethod !== 'cash' && proofImage ? 'Verifying screenshot…' : 'Processing…'}
                  </>
                ) : (
                  <>{isPaidThisMonth ? 'PAID - Collection Disabled' : 'Confirm Collection'}</>
                )}
              </button>
            </form>
          </div>
        </div>

      </div>

      {/* Installation Invoice Modal */}
      {showInvoice && installInvoice && (
        <InstallationInvoiceView
          invoice={installInvoice}
          onClose={() => setShowInvoice(false)}
        />
      )}
    </div>
  );
};

export default QuickCollection;
